import { gql, TypedDocumentNode } from '@apollo/client';
import { PartnerHeroStats } from '../graphTypes';

export const QUERY_GET_PARTNERS = gql`
  query GetPartners {
    deployment {
      name
    }
    partners {
      id
      name
      primaryDeployment
    }
  }
`;

export const QUERY_GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      role
      partnerIds
      name
    }
  }
`;

/*
HERO STATS -----------------------
 */

export const getAverageHealthScoreHeroQuery = (): any => {
  return gql`
    query latest_typical_stats {
      signal_3_global_stats_latest {
        average_health_score
      }
    }
  `;
};

export const getPartnerLatestStatsHeroQuery = (
  partnerId: string,
): TypedDocumentNode<{
  signal_3_partner_stats_latest: PartnerHeroStats[];
}> => {
  return gql`
  query latest_partner_stats {
    signal_3_partner_stats_latest (where: {partner_id: {_eq: "${partnerId}"}}) {
  average_health_score
  location_count
  any_severity_location_count
  maximum_severity_location_count
  suppressed_location_count
 }
}
`;
};

export const getPartnerDailyFactHeroQuery = (
  partnerId: string,
  ts: string,
): any => {
  return gql`
    query recent_partner_stats {
      signal_3_partner_stats_daily_fact(where: {partner_id: {_eq: "${partnerId}"}, ts: { _eq: "${ts}"}}) {
        ts
        average_health_score
        maximum_severity_location_count
      }
    }
  `;
};

/*
TREND CHART -----------------------
 */

export const getQueryPartnerAverageHealthScore = (partnerId: string): any => {
  return gql`
  query latest_partner_stats {
    signal_3_partner_stats_latest (where: {partner_id: {_eq: "${partnerId}"}}) {
  health_score_distribution
}
}
`;
};

export const getQueryTypicalAverageHealthScore = (): any => {
  return gql`
    query latest_typical_stats {
      signal_3_global_stats_latest {
        health_score_distribution
      }
    }
  `;
};

export const getQueryTypicalHealthScoreInRange = (): any => {
  return gql`
    query latest_typical_stats_in_health_score_range {
      baselineHealthScoreBreakdown: signal_3_global_stats_latest {
        environment_category_location_count
        stability_category_location_count
        network_speed_category_location_count
        internet_connection_category_location_count
        device_qoe_category_location_count
        pod_qoe_category_location_count
        wifi_coverage_category_location_count
        offline_network_location_count
        csp_outage_location_count
        too_many_optimizations_location_count
        failed_optimizations_location_count
        device_qoe_location_count
        pod_qoe_location_count
        download_volatile_location_count
        download_low_location_count
        offline_pod_location_count
        evil_twin_location_count
        parallel_network_location_count
        pod_placement_location_count
        coverage_location_count
        thermal_reboot_location_count
        non_thermal_reboot_location_count
      }
    }
  `;
};

export const getQueryPartnerHealthScoreInRange = (partnerId: string): any => {
  return gql`
  query latest_typical_stats_in_health_score_range {
    partnerHealthScoreBreakdown: signal_3_partner_stats_latest (
      where: {
        partner_id: {_eq: "${partnerId}"}
      }
    ) {
      environment_category_location_count
      stability_category_location_count
      network_speed_category_location_count
      internet_connection_category_location_count
      device_qoe_category_location_count
      pod_qoe_category_location_count
      wifi_coverage_category_location_count
      offline_network_location_count
      csp_outage_location_count
      too_many_optimizations_location_count
      failed_optimizations_location_count
      device_qoe_location_count
      pod_qoe_location_count
      download_volatile_location_count
      download_low_location_count
      offline_pod_location_count
      evil_twin_location_count
      parallel_network_location_count
      pod_placement_location_count
      coverage_location_count
      thermal_reboot_location_count
      non_thermal_reboot_location_count
    }
  }
  `;
};

/**
 * Timeline Interactions
 */

export const getPartnerHealthScoreTimelineQuery = (
  partnerId: string,
  limit: string,
): any => {
  return gql`
    query partner_health_score_timeline {
      healthScoreTimeline: signal_3_partner_stats_daily_fact (
          where: {
            partner_id: {_eq: "${partnerId}"}
          },
          order_by: {ts: desc},
          limit: ${limit}
        ) {
        ts
        average_health_score
      }
    }
  `;
};

export const getTypicalHealthScoreTimelineQuery = (limit: string): any => {
  return gql`
    query typical_health_score_timeline {
      healthScoreTimeline: signal_3_global_stats_daily_fact(
        order_by: { ts: desc },
        limit: ${limit}
      ) {
        ts
        average_health_score
      }
    }
  `;
};

export const getHourlyPartnerHealthScoreTimelineQuery = (
  partnerId: string,
): any => {
  return gql`
    query partner_health_score_timeline {
      healthScoreTimeline: signal_3_partner_stats_hourly_fact (
          where: {
            partner_id: {_eq: "${partnerId}"}
          },
          order_by: { ts: desc },
          limit: 24
        ) {
        ts
        average_health_score
      }
    }
  `;
};

export const getHourlyTypicalHealthScoreTimelineQuery = (): any => {
  return gql`
    query typical_health_score_timeline {
      healthScoreTimeline: signal_3_global_stats_hourly_fact(
        order_by: { ts: desc }
        limit: 24
      ) {
        ts
        average_health_score
      }
    }
  `;
};

export const getTypicalIssuesQuery = (limit: string, latest: boolean): any => {
  return gql`
    query typical_stats {
      typicalIssues: signal_3_global_stats_${
        latest ? 'latest' : limit === '1' ? 'hourly_fact' : 'daily_fact'
      }
      ${
        latest
          ? ''
          : `(
        order_by:{ts:desc},
        limit: ${limit === '1' ? '24' : limit}
      )`
      }
      {
        ${!latest ? 'ts' : ''}
        environment_category_location_count
        stability_category_location_count
        network_speed_category_location_count
        internet_connection_category_location_count
        device_qoe_category_location_count
        pod_qoe_category_location_count
        wifi_coverage_category_location_count
        offline_network_location_count
        csp_outage_location_count
        too_many_optimizations_location_count
        failed_optimizations_location_count
        device_qoe_location_count
        pod_qoe_location_count
        download_volatile_location_count
        download_low_location_count
        offline_pod_location_count
        evil_twin_location_count
        parallel_network_location_count
        pod_placement_location_count
        coverage_location_count
        thermal_reboot_location_count
      }
    }
  `;
};

export const getPartnerIssuesQuery = (
  partnerId: string,
  limit: string,
  latest: boolean,
): any => {
  return gql`
    query partner_stats {
      partnerIssues: signal_3_partner_stats_${
        latest ? 'latest' : limit === '1' ? 'hourly_fact' : 'daily_fact'
      } (
        where: {
          partner_id: { _eq: "${partnerId}" }
        },
        ${
          latest
            ? ''
            : `
            order_by:{ts:desc},
            limit: ${limit === '1' ? '24' : limit}
            `
        })
      {
        ${!latest ? 'ts' : ''}
        environment_category_location_count
        stability_category_location_count
        network_speed_category_location_count
        internet_connection_category_location_count
        device_qoe_category_location_count
        pod_qoe_category_location_count
        wifi_coverage_category_location_count
        location_count
        offline_network_location_count
        csp_outage_location_count
        too_many_optimizations_location_count
        failed_optimizations_location_count
        device_qoe_location_count
        pod_qoe_location_count
        download_volatile_location_count
        download_low_location_count
        offline_pod_location_count
        evil_twin_location_count
        parallel_network_location_count
        pod_placement_location_count
        coverage_location_count
        thermal_reboot_location_count
      }
    }
  `;
};

export const getQueryHealthDownload = (
  partnerId: string,
  loadAccountId: boolean,
): any => {
  return gql`
    query download_query {
      signal_3_location_stats_latest(where: {partner_id: {_eq: "${partnerId}"}}) {
          ${loadAccountId ? 'account_id' : ''}
          location_id
          customer_id
          partner_id
          health_score
          latitude
          longitude
          ts
          unfinished_onboarding
          csp_outage_severity
          offline_network_severity
          offline_pod_severity
          evil_twin_severity
          thermal_reboot_severity
          parallel_network_severity
          coverage_severity
          pod_placement_severity
          pod_qoe_severity
          device_qoe_severity
          download_low_severity
          download_volatile_severity
          too_many_optimizations_severity
          failed_optimizations_severity
      }
    }
  `;
};

export const getQueryIssueMatrix = (partnerId: string): any => {
  return gql`
  query IssueMatrixQuery {
      issueMatrixStats: signal_3_p_partner_issues_latest(args: {p_partner_id: "${partnerId}"}) {
        issue_category
        issue_type
        location_count
        non_onboarded_location_count
      }
  }`;
};

export const getQuerySuppressedLocations = (): TypedDocumentNode<
  {
    result: {
      location_id: string;
      account_id: string;
      customer_id: string;
      n_days_offline: number;
      offline_since: string;
    }[];
  },
  {
    partnerId: string;
  }
> => gql`
  query signal_3_p_partner_suppressed_locations_latest($partnerId: String!) {
    result: signal_3_p_partner_suppressed_locations_latest(
      args: { p_partner_id: $partnerId }
    ) {
      location_id
      account_id
      customer_id
      n_days_offline
      offline_since
    }
  }
`;
